import { ErrorMessage } from '@hookform/error-message'
import {
  Upload,
  type UploadFileInfo,
  type UploadProps,
} from '@progress/kendo-react-upload'
import React from 'react'
import { type Control, useFormState } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { type z } from 'zod'

import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { useFormField } from '~/hooks/useFormField'
import { compact } from '~/utils'

import { type fileInfoSchema } from '../utils'

import { DataSettingsCardSection } from './DataSettingsCardSection'
import styles from './UploadFileSection.css'

type InputValues = z.infer<typeof fileInfoSchema>

export type UploadFileSectionProps<T extends InputValues> =
  React.PropsWithChildren<{
    control: Control<T>
    extraFormLabel?: string
    onFileInfoChange?: (fileInfo: UploadFileInfo | undefined) => void
    onUpload: () => void
  }>

export const UploadFileSection = <T extends InputValues>({
  children,
  control: controlProp,
  extraFormLabel,
  onFileInfoChange,
  onUpload,
}: UploadFileSectionProps<T>) => {
  const { t } = useTranslation()
  const control = controlProp as Control<InputValues>
  const { errors } = useFormState({ control })
  const fileInfoField = useFormField(control, 'fileInfo')

  const files = React.useMemo<UploadProps['files']>(
    () => compact([fileInfoField.value]),
    [fileInfoField.value],
  )

  const handleAdd = React.useCallback<EH<UploadProps, 'onAdd'>>(
    (event) => {
      const fileInfo = event.newState[0]
      fileInfoField.onChange(fileInfo)
      onFileInfoChange?.(fileInfo)
    },
    [fileInfoField, onFileInfoChange],
  )

  const handleRemove = React.useCallback<EH<UploadProps, 'onRemove'>>(
    (event) => {
      const fileInfo = event.newState[0]
      fileInfoField.onChange(fileInfo)
      onFileInfoChange?.(fileInfo)
    },
    [fileInfoField, onFileInfoChange],
  )

  const handleUploadButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    onUpload()
  }, [onUpload])

  return (
    <DataSettingsCardSection title={t('upload_data')}>
      <VStack spacing={4}>
        <VStack spacing={1}>
          <Typography fontSize={14} variant="normal">
            {t('file')}
          </Typography>

          <Upload
            autoUpload={false}
            className={styles.upload}
            files={files}
            multiple={false}
            showActionButtons={false}
            onAdd={handleAdd}
            onRemove={handleRemove}
          />

          <ErrorMessage
            errors={errors}
            name="fileInfo"
            render={({ message }) => (
              <Typography color="error" variant="normal">
                {message}
              </Typography>
            )}
          />
        </VStack>

        <VStack spacing={1}>
          <Typography fontSize={14} variant="normal">
            {extraFormLabel}
          </Typography>

          {children}
        </VStack>

        <StyledButton
          buttonStyle="success"
          className={styles.uploadButton}
          onClick={handleUploadButtonClick}
        >
          {t('upload')}
        </StyledButton>
      </VStack>
    </DataSettingsCardSection>
  )
}
